import { useState } from 'react';
import { Stack, Typography, Divider, Box, Container, Grid, Card, Select, FormControl, MenuItem } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { LoadingButton } from '@mui/lab';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import axiosInstance from '../utils/axiosInstance';
import EncodeSettings from '../components/encode-settings/encodeSettings';
import ImageSettings from '../components/image-settings/imageSettings';
import SingleSelector from '../components/single-selector/singleSelector';
import VideoPlayer from '../components/video-player/videoPlayer';

export default function SensorConfiguration() {
  const { enqueueSnackbar } = useSnackbar();
  const [sensor, setSensor] = useState(null);
  const [encoding, setEncoding] = useState('');
  const [sensorSettings, setSensorSettings] = useState(null);
  const [initialSettings, setInitialSettings] = useState(null);
  const [disableH264, setDisableH264] = useState(false);
  const [disableH265, setDisableH265] = useState(false);
  const [fetchingNewSettings, setFetchingNewSettings] = useState(false);
  const [fetchStatus, setFetchStatus] = useState('uninitialized');
  const sensorList = useSelector((state) => state.app.sensorList);


  const parseSettings = (settings) => {
    const encodingType = settings?.Encode?.Encoding?.Value || '';
    console.log("encodingType: ", encodingType)
    setEncoding(encodingType);
    let encodeSettings = null;
    if (settings?.Encode?.Options) {
      const options = settings.Encode.Options;
      const index = options.length > 1 && encodingType !== 'H264' ? 1 : 0;
      encodeSettings = options[index]?.[encodingType] || null;
      if (index === 0 && settings?.Encode?.Options?.length <= 1) {
        if (encodingType === 'H264') {
          setDisableH265(true);
          console.log("disabled h265")
        }
        else {
          setDisableH264(true);
          console.log("disabled h264")
        }
      }
    }

    console.log("encodeSettings: ", encodeSettings)
    if (encodeSettings) {
      const imageSettings = settings?.Image || null;
      console.log("imageSettings: ", imageSettings)
      if (imageSettings) {
        const newSettings = {
          Image: imageSettings,
          Encode: encodeSettings
        }
        setSensorSettings(newSettings);
      }
    }
  }

  const fetchSensorSettings = (sensor) => {
    if (!sensor) {
      return;
    }
    setFetchStatus('pending');
    axiosInstance
      .get(`${window.location.protocol}//${sensor.host}/api/v1/sensor/${sensor.sensorId}/settings`,{ headers: { "streamId": sensor.sensorId } })
      .then(async (response) => {
        setFetchStatus('success');
        setDisableH264(false);
        setDisableH265(false);
        console.debug('fetchSettings response: ', response.data);
        if (Object.prototype.hasOwnProperty.call(response.data, sensor.sensorId)) {
          parseSettings(response.data[sensor.sensorId])
          setInitialSettings(response.data[sensor.sensorId]);
        } else {
          setInitialSettings(null);
        }
      })
      .catch((error) => {
        setFetchStatus('error');
        if (error.response.data == null)
          enqueueSnackbar(`Error - ${error.response.status}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
        else
          enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
      });
  };

  const handleEncodeSettingModified = (property, value) => {
    console.debug(property, value);
    const newDeviceSettings = JSON.parse(JSON.stringify(sensorSettings));
    newDeviceSettings.Encode[property].Value = property !== 'Resolution' ? value.toString() : value;
    console.debug(newDeviceSettings);
    setSensorSettings(newDeviceSettings);
  };

  const handleImageSettingModified = (property, value) => {
    console.debug(property, value);
    const newDeviceSettings = JSON.parse(JSON.stringify(sensorSettings));
    newDeviceSettings.Image[property].Value = value.toString();
    console.debug(newDeviceSettings);
    setSensorSettings(newDeviceSettings);
  };

  const handleSubmitSettings = () => {
    const updatedSettings = JSON.parse(JSON.stringify(sensorSettings));
    Object.keys(updatedSettings).forEach((group) => {
      const settingGroup = updatedSettings[group];
      Object.keys(settingGroup).forEach((name) => {
        settingGroup[name] = settingGroup[name].Value;
      });
    });
    console.debug('Update settings payload', updatedSettings);
    if (updatedSettings && updatedSettings.Encode) {
      updatedSettings.Encode.Encoding = encoding;
    }
    enqueueSnackbar(`Settings update pending: ${sensor.name}`, {
      variant: 'info',
    });
    axiosInstance
      .post(`${window.location.protocol}//${sensor.host}/api/v1/sensor/${sensor.sensorId}/settings`, updatedSettings,{ headers: { "streamId": sensor.sensorId } })
      .then((response) => {
        const { data } = response;
        enqueueSnackbar(`Settings updated: ${sensor.name}`, {
          variant: 'success',
        });
        console.debug('postSettings response: ', data);
        setFetchingNewSettings(true);
        fetchSensorSettings(sensor)
        setTimeout(() => {
          setFetchingNewSettings(false);
        }, 5000);
      })
      .catch((error) => {
        enqueueSnackbar(`Settings update failed: ${sensor.name}`, {
          variant: 'error',
        });
        console.error(error);
      });
  };

  const parseSettingsAgain = (selectedEncodingType) => {
    const encodeSettings = initialSettings?.Encode?.Options[selectedEncodingType === 'H264' ? 0 : 1][selectedEncodingType] || null;

    if (encodeSettings && encodeSettings.Bitrate && encodeSettings.Bitrate.Value) {
      encodeSettings.Bitrate.Value = encodeSettings.Bitrate.Value.toString();
    }
    if (encodeSettings && encodeSettings.EncodingInterval && encodeSettings.EncodingInterval.Value) {
      encodeSettings.EncodingInterval.Value = encodeSettings.EncodingInterval.Value.toString();
    }
    if (encodeSettings && encodeSettings.FrameRate && encodeSettings.FrameRate.Value) {
      encodeSettings.FrameRate.Value = encodeSettings.FrameRate.Value.toString();
    }
    if (encodeSettings && encodeSettings.GovLength && encodeSettings.GovLength.Value) {
      encodeSettings.GovLength.Value = encodeSettings.GovLength.Value.toString();
    }
    if (encodeSettings && encodeSettings.Profiles && encodeSettings.Profiles.Value) {
      encodeSettings.Profiles.Value = encodeSettings.Profiles.Value.toString();
    }
    if (encodeSettings && encodeSettings.Quality && encodeSettings.Quality.Value) {
      encodeSettings.Quality.Value = encodeSettings.Quality.Value.toString();
    }
    if (encodeSettings && encodeSettings.Resolution && encodeSettings.Resolution.Value && encodeSettings.Resolution.Value.Width && encodeSettings.Resolution.Value.Height) {
      encodeSettings.Resolution.Value = {
        Width: encodeSettings.Resolution.Value.Width.toString(),
        Height: encodeSettings.Resolution.Value.Height.toString()
      };
    }

    console.log("default encode settings: ", encodeSettings)
    if (encodeSettings) {
      const imageSettings = initialSettings?.Image || null;
      console.log("imageSettings: ", imageSettings)
      if (imageSettings) {
        const newSettings = {
          Image: imageSettings,
          Encode: encodeSettings
        }
        setSensorSettings(null)
        setTimeout(() => {
          setSensorSettings(newSettings);
        }, 400);
      }
    }
  }

  const handleEncodingChange = (event) => {
    const newEncoding = event.target.value;
    setEncoding(newEncoding);

    parseSettingsAgain(newEncoding);
  }

  const handleSensorSelect = (cam) => {
    setSensor(cam);
    setSensorSettings(null);
    fetchSensorSettings(cam);
    if (!cam) {
      setSensorSettings(null);
    }
  };

  // Function to filter authorized sensors
  const filterAuthorized = (sensorList) => sensorList
    .filter((sensor) => sensor.isAuthorized === true);

  const encodeSettingsPresent =
    sensorSettings != null && Object.prototype.hasOwnProperty.call(sensorSettings, 'Encode');
  const imageSettingsPresent = sensorSettings != null && Object.prototype.hasOwnProperty.call(sensorSettings, 'Image');

  return (
    <>
      <Helmet>
        <title> Sensor configuration </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Sensor configuration
        </Typography>
        <Grid container spacing={3}>
          {
            !fetchingNewSettings &&
            <><Grid item xs={12} sm={12} md={12}>
              <Card>
                <Box sx={{ p: 3 }}>
                  <Stack spacing={3} sx={{ mb: 2 }}>
                    <SingleSelector
                      id="sensor-configuration"
                      placeholder="Type to search"
                      label="Select sensor"
                      value={sensor}
                      onChange={handleSensorSelect}
                      getOptionLabel={(option) => {
                        if (option.remoteDeviceName && option.remoteDeviceLocation) {
                          return `${option.name} at ${option.remoteDeviceName} (${option.remoteDeviceLocation})`;
                        } if (option.remoteDeviceName) {
                          return `${option.name} at ${option.remoteDeviceName}`;
                        }
                        return option.name;

                      }}
                      options={filterAuthorized(sensorList)} />
                    {sensor != null && encodeSettingsPresent && fetchStatus !== 'pending' && (
                      <Alert severity="success">
                        {Object.keys(sensorSettings.Encode).length} encode settings found for primary stream {sensor.name}
                      </Alert>
                    )}
                    {sensor != null && imageSettingsPresent && fetchStatus !== 'pending' && (
                      <Alert severity="success">
                        {Object.keys(sensorSettings.Image).length} image settings found for primary stream {sensor.name}
                      </Alert>
                    )}
                    {sensor != null && imageSettingsPresent == null && fetchStatus !== 'pending' && (
                      <Alert severity="error">Image settings not found for {sensor.name}</Alert>
                    )}
                    {sensor != null && encodeSettingsPresent == null && fetchStatus !== 'pending' && (
                      <Alert severity="error">Encode settings not found for {sensor.name}</Alert>
                    )}
                  </Stack>
                </Box>
              </Card>
            </Grid><Grid item xs={12} sm={12} md={12}>
                <Card>
                  <Box sx={{ p: 3 }}>
                    <Divider sx={{ my: 2 }}>
                      <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                        Live preview
                      </Typography>
                    </Divider>
                    {sensor && (
                      <Box
                        sx={{
                          display: 'block',
                          marginLeft: 'auto',
                          marginRight: 'auto',
                          width: { xs: '100%', sm: '100%', md: 600, lg: 700 },
                        }}
                      >
                        <VideoPlayer
                          sensor={sensor}
                          key={`${sensor.sensorId + sensor.host + sensor.name}_vid_config`}
                          page="configuration"
                          onlyVideo
                        />
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Card>
                  <Box sx={{ p: 3 }}>
                    <Stack spacing={3} sx={{ mb: 2 }}>
                      <Divider sx={{ my: 2 }}>
                        <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                          Image settings
                        </Typography>
                      </Divider>
                      {imageSettingsPresent && (
                        <>
                          <ImageSettings
                            imageSettings={sensorSettings.Image}
                            handleSettingModified={handleImageSettingModified} />
                          <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
                            <LoadingButton size="large" variant="contained" onClick={handleSubmitSettings}>
                              Update
                            </LoadingButton>
                          </Box>
                        </>
                      )}
                    </Stack>
                  </Box>
                </Card>
              </Grid><Grid item xs={12} sm={12} md={6}>
                <Card>
                  <Box sx={{ p: 3 }}>
                    <Stack spacing={3} sx={{ mb: 2 }}>
                      <Divider sx={{ my: 2 }}>
                        <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                          Encode settings
                        </Typography>
                      </Divider>
                      {sensor &&
                        <FormControl fullWidth>
                          <Typography>
                            Encoding
                          </Typography>
                          <Select value={encoding} onChange={handleEncodingChange}>
                            <MenuItem value="H264" disabled={disableH264} >H264</MenuItem>
                            <MenuItem value="H265" disabled={disableH265} >H265</MenuItem>
                          </Select>
                        </FormControl>}
                      {encodeSettingsPresent && (
                        <>
                          <EncodeSettings
                            encodeSettings={sensorSettings.Encode}
                            handleSettingModified={handleEncodeSettingModified} />
                          <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
                            <LoadingButton size="large" variant="contained" onClick={handleSubmitSettings}>
                              Update
                            </LoadingButton>
                          </Box>
                        </>
                      )}
                    </Stack>
                  </Box>
                </Card>
              </Grid></>
          }
          {
            fetchingNewSettings &&
            <Grid item xs={12} sm={12} md={12}>
              <Card>
                <Box sx={{ p: 3 }}>
                  <LinearProgress />

                </Box>
              </Card>
            </Grid>

          }
        </Grid>
      </Container>
    </>
  );
}
